<template>
  <SubHeading :home="home" :tabs="tabs" :shorts="false" :currTab="currTab" @active="handleActive" />

  <div class="progress container" v-if="isLoading && isReady">
    <div class="indeterminate"></div>
  </div>

  <nav class="sba-collection-breadcrumb z-depth-0">
    <div class="container nav-wrapper">
      <div class="sba-collection-breadcrumb-row">
        <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
          <i class="material-icons">home</i>
          <span>Dashboard</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <span class="breadcrumb sba-collection-crumb blue-grey-text pointable">
          <span>Tariff Mini</span>
        </span>
        <span class="sba-collection-context">Provider Tariff Register</span>
      </div>
    </div>
  </nav>

  <div id="view"></div>

  <div class="sba-collection-page-surface is-workbench tariff-mini-page-surface">
    <div class="container">
      <LoadingDiv v-if="!isReady" />

      <div v-else>
        <div class="section tariff-mini-list-surface sba-slide-in">
          <div class="tariff-mini-review-head">
            <div class="tariff-mini-title-block">
              <label class="active teal-text">Tariff Mini</label>
              <h5>Provider Tariff Register</h5>
              <p>Open a provider tariff shelf and adjust agreed item prices only.</p>
            </div>
          </div>

          <div class="tariff-mini-search-card">
            <form>
              <div class="input-field">
                <input id="search" type="search" v-model="searchItem" placeholder="Search provider by name or address" autocomplete="off">
                <label class="label-icon" for="search"><i class="material-icons">search</i></label>
                <i class="material-icons" @click="handleClearSearch" v-if="searchItem">clear</i>
              </div>
            </form>
          </div>

          <div class="tariff-mini-search-status" v-if="searchItem">
            <div>
              <span>Search active</span>
              <strong>{{ searchItem }}</strong>
            </div>
            <button type="button" @click="handleClearSearch" title="clear search">
              <i class="material-icons">close</i>
            </button>
          </div>

          <router-link
            class="card z-depth-0 tariff-mini-provider-card"
            v-for="provider in providers"
            :key="provider.id"
            :to="{ name: 'tariffMiniView', params: { id: provider.id } }"
          >
            <div class="card-content">
              <div class="tariff-mini-provider-meta">
                <span class="tariff-mini-provider-status" :class="providerStatusClass(provider)">
                  {{ providerStatusLabel(provider) }}
                </span>
                <span class="tariff-mini-provider-count" v-if="provider.encounters_count">
                  {{ provider.encounters_count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }} claims
                </span>
              </div>
              <h5>{{ provider.name }}</h5>
              <p>
                <span v-if="provider?.phone">{{ provider.phone }}</span>
                <span v-if="provider?.phone && provider?.email"> · </span>
                <span v-if="provider?.email">{{ provider.email }}</span>
              </p>
              <small>{{ provider.address || "No address recorded" }}</small>
              <i class="material-icons tariff-mini-open-icon">arrow_forward</i>
            </div>
          </router-link>
        </div>

        <div class="section center tariff-mini-empty-state" v-if="!providers.length">
          <i class="material-icons">price_change</i>
          <h6>No providers found</h6>
          <p>There are no provider records matching this tariff view.</p>
        </div>

        <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-else />
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from "vue";
import useModuleNavigation from "@/composables/useModuleNavigation";
import getItems from "@/composables/getItems";
import SubHeading from "@/components/HeaderSubDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import { debounce, getCachedPayload, setCachedPayload } from "@/composables/requestOptimization";

export default {
  name: "tariffMiniHome",
  components: { SubHeading, LoadingDiv, PaginationDiv },
  setup() {
    const response = ref(null);
    const isReady = ref(false);
    const isLoading = ref(true);
    const currUrl = ref(null);
    const currHook = ref(null);
    const error = ref(null);
    const searchItem = ref(null);
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);
    const providerSearchCacheTtl = 60 * 1000;
    let providerSearchRequestId = 0;

    const { routeToTab } = useModuleNavigation();

    onMounted(() => {
      document.title = "Tariff Mini";
    });

    onMounted(() => {
      tabs.value.push({ id: 1, name: "Tariff Mini", title: "All", icon: null, url: "tariffMini", hash: null, });
      tabs.value.push({ id: 2, name: "Tariff Mini", title: "View", icon: "visibility", url: "tariffMini", hash: "#view", });

      currTab.value = tabs.value.find(x => x.id === 1);
      home.value = tabs.value.find(x => x.id === 1);
    });

    const handleActive = (tab) => {
      routeToTab(tab, { replace: tab?.url === "tariffMini" && !tab?.hash });
    };

    const getItemsFromApi = (table) => {
      (async () => {
        const { res, err } = await getItems(table);

        if (res.value) {
          response.value = res.value.data;
        } else {
          error.value = err.value;
        }

        isReady.value = true;
        isLoading.value = false;
      })();
    };

    onMounted(() => {
      currUrl.value = "provider";
      currHook.value = "?";
      getItemsFromApi(currUrl.value);
    });

    const handleClearSearch = () => {
      providerSearchRequestId++;
      isLoading.value = true;
      searchItem.value = null;
      currUrl.value = "provider";
      currHook.value = "?";
      getItemsFromApi(currUrl.value);
    };

    const loadProviderSearch = debounce(async (query, requestId) => {
      if (requestId !== providerSearchRequestId || query !== searchItem.value?.trim()) {
        return;
      }

      const url = `provider/list/search?q=${encodeURIComponent(query)}`;
      const cachedPayload = getCachedPayload(url);

      if (cachedPayload) {
        response.value = cachedPayload;
        isReady.value = true;
        isLoading.value = false;
        return;
      }

      const { res, err } = await getItems(url);

      if (requestId !== providerSearchRequestId || query !== searchItem.value?.trim()) {
        return;
      }

      if (res.value) {
        response.value = res.value.data;
        setCachedPayload(url, res.value.data, providerSearchCacheTtl);
      } else {
        error.value = err.value;
      }

      isReady.value = true;
      isLoading.value = false;
    });

    const handlePage = (page) => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      isReady.value = false;
      getItemsFromApi(`${currUrl.value}${currHook.value}page=${page}`);
    };

    watch(searchItem, (value) => {
      const query = value?.trim() || "";
      providerSearchRequestId++;

      if (!query) {
        return;
      }

      if (query.length < 2) {
        isLoading.value = false;
        return;
      }

      isLoading.value = true;
      currUrl.value = `provider/list/search?q=${encodeURIComponent(query)}`;
      currHook.value = "&";
      loadProviderSearch(query, providerSearchRequestId);
    });

    const providerStatusLabel = (provider) => {
      if (provider?.status === "s") {
        return "Inactive";
      }

      if (provider?.status === "p") {
        return "Pending";
      }

      return "Active";
    };

    const providerStatusClass = (provider) => {
      if (provider?.status === "s") {
        return "is-inactive";
      }

      if (provider?.status === "p") {
        return "is-pending";
      }

      return "is-active";
    };

    const providers = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data;
      }
    });

    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value;
      }
    });

    return {
      handleActive,
      home,
      currTab,
      tabs,
      handleClearSearch,
      handlePage,
      providerStatusClass,
      providerStatusLabel,
      isReady,
      isLoading,
      error,
      searchItem,
      providers,
      allPages,
    };
  },
};
</script>

<style scoped>
.tariff-mini-page-surface {
  min-height: calc(100vh - 260px);
  padding-bottom: 56px;
  padding-top: 0;
}

.tariff-mini-list-surface,
.tariff-mini-list-surface.section {
  margin-top: -14px;
  padding-bottom: 0;
  padding-top: 0;
  position: relative;
  z-index: 1;
}

.tariff-mini-review-head,
.tariff-mini-search-card,
.tariff-mini-search-status,
.tariff-mini-provider-card,
.tariff-mini-empty-state {
  box-sizing: border-box;
  width: 100%;
}

.tariff-mini-review-head,
.tariff-mini-search-card,
.tariff-mini-search-status,
.tariff-mini-provider-card,
.tariff-mini-empty-state,
.tariff-mini-page-surface :deep(.pagination) {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.tariff-mini-review-head {
  align-items: flex-end;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px 6px 0 0;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: 0 12px 20px -18px rgba(38, 50, 56, 0.55);
  display: flex;
  gap: 22px;
  justify-content: space-between;
  margin-bottom: 0;
  overflow: visible;
  padding: 24px 26px;
  position: relative;
  z-index: 2;
}

.tariff-mini-review-head::before {
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 7px 7px 0 0;
  content: "";
  height: 18px;
  left: -1px;
  pointer-events: none;
  position: absolute;
  right: -1px;
  top: -1px;
  z-index: 1;
}

.tariff-mini-title-block {
  position: relative;
  z-index: 2;
}

.tariff-mini-title-block h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.tariff-mini-title-block p {
  color: #607d8b;
  line-height: 1.45;
  margin: 6px 0 0;
  max-width: 720px;
}

.tariff-mini-search-card {
  background: #f8fafb;
  border: 1px solid #c4d3d9;
  border-radius: 0 0 6px 6px;
  border-top: 0;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.78);
  margin-bottom: 16px;
  padding: 12px 16px;
}

.tariff-mini-search-card:has(+ .tariff-mini-search-status) {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: none;
  margin-bottom: 0;
}

.tariff-mini-search-card form,
.tariff-mini-search-card .input-field {
  margin: 0;
}

.tariff-mini-search-card .input-field {
  position: relative;
}

.tariff-mini-search-card input[type="search"] {
  background: #ffffff;
  border: 1px solid #d5e1e5 !important;
  border-radius: 6px;
  box-sizing: border-box;
  box-shadow: none !important;
  color: #263238;
  font-weight: 600;
  height: 48px;
  line-height: 48px;
  margin: 0;
  padding-left: 46px;
  padding-right: 42px;
  width: 100%;
}

.tariff-mini-search-card input[type="search"]:focus {
  border-color: #80cbc4 !important;
  box-shadow: 0 0 0 3px rgba(128, 203, 196, 0.18) !important;
}

.tariff-mini-search-card .label-icon {
  align-items: center;
  display: flex;
  height: 48px;
  left: 10px;
  line-height: 48px;
  position: absolute;
  top: 0;
}

.tariff-mini-search-card .material-icons {
  color: #00897b;
  height: 48px;
  line-height: 48px;
}

.tariff-mini-search-card .input-field > i {
  align-items: center;
  cursor: pointer;
  display: flex;
  height: 48px;
  line-height: 48px;
  position: absolute;
  right: 10px;
  top: 0;
}

.tariff-mini-search-status {
  align-items: center;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 0 0 6px 6px;
  border-top: 0;
  box-shadow: 0 16px 28px -24px rgba(38, 50, 56, 0.72);
  color: #263238;
  display: flex;
  justify-content: space-between;
  margin: 0 0 16px;
  padding: 12px 14px 12px 16px;
}

.tariff-mini-search-status span {
  color: #607d8b;
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 5px;
  text-transform: uppercase;
}

.tariff-mini-search-status strong {
  color: #263238;
  font-size: 0.98rem;
}

.tariff-mini-search-status button {
  align-items: center;
  background: #f7f7f7;
  border: 1px solid #e1e8eb;
  border-radius: 50%;
  color: #455a64;
  cursor: pointer;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  padding: 0;
  width: 32px;
}

.tariff-mini-search-status button i {
  font-size: 18px;
}

.tariff-mini-provider-card {
  background: linear-gradient(180deg, #ffffff 0%, #fdfdfd 100%);
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9), 0 18px 32px -30px rgba(38, 50, 56, 0.72) !important;
  color: inherit;
  cursor: pointer;
  display: block;
  text-decoration: none;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.tariff-mini-provider-card:hover {
  border-color: #80cbc4;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9), 0 18px 32px -28px rgba(0, 105, 92, 0.34) !important;
  transform: translateY(-1px);
}

.tariff-mini-provider-card .card-content {
  background: transparent;
  padding-right: 78px;
  position: relative;
}

.tariff-mini-provider-card h5 {
  color: #263238;
  font-size: 1.22rem;
  font-weight: 700;
  line-height: 1.35;
  margin: 8px 0 7px;
}

.tariff-mini-provider-card p {
  color: #455a64;
  margin: 0 0 5px;
}

.tariff-mini-provider-card small {
  color: #607d8b;
  display: block;
  line-height: 1.45;
}

.tariff-mini-provider-meta {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tariff-mini-provider-status,
.tariff-mini-provider-count {
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1.1;
  padding: 7px 10px;
  text-transform: uppercase;
}

.tariff-mini-provider-status.is-active {
  background: #e8f3f2;
  border: 1px solid #9bd2cb;
  color: #00695c;
}

.tariff-mini-provider-status.is-inactive {
  background: #ffebee;
  border: 1px solid #ffcdd2;
  color: #b71c1c;
}

.tariff-mini-provider-status.is-pending {
  background: #fff8e1;
  border: 1px solid #ffe082;
  color: #795548;
}

.tariff-mini-provider-count {
  background: #f8fafb;
  border: 1px solid #d5e1e5;
  color: #455a64;
}

.tariff-mini-open-icon {
  align-items: center;
  background: #f8fafb;
  border: 1px solid #d5e1e5;
  border-radius: 50%;
  color: #00897b;
  display: inline-flex;
  font-size: 20px;
  height: 38px;
  justify-content: center;
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  width: 38px;
}

.tariff-mini-empty-state {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 16px 28px -28px rgba(38, 50, 56, 0.72);
  color: #607d8b;
  margin-top: 16px;
  padding: 34px 20px;
}

.tariff-mini-empty-state i {
  align-items: center;
  background: #e0f2f1;
  border: 1px solid #80cbc4;
  border-radius: 50%;
  color: #00897b;
  display: inline-flex;
  font-size: 24px;
  height: 48px;
  justify-content: center;
  margin-bottom: 10px;
  width: 48px;
}

.tariff-mini-empty-state h6 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 6px;
}

.tariff-mini-empty-state p {
  margin: 0;
}

.pointable {
  cursor: default;
}

@media only screen and (max-width: 700px) {
  .tariff-mini-review-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .tariff-mini-provider-card .card-content {
    padding-right: 24px;
  }

  .tariff-mini-open-icon {
    margin-top: 16px;
    position: static;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tariff-mini-review-head,
  .tariff-mini-search-card,
  .tariff-mini-search-status,
  .tariff-mini-provider-card,
  .tariff-mini-empty-state,
  .tariff-mini-page-surface :deep(.pagination) {
    animation: none;
  }
}
</style>
